import React, { useEffect, useState } from "react";
import {
  Table,
  Card,
  Button,
  Space,
  Modal,
  Spin,
  Descriptions,
  Input,
  message,
  DatePicker,
  Typography,
  Select,
} from "antd";
import {
  SearchOutlined,
  CloseOutlined,
  FilePdfOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import useApis from "../apis/useApis";
import appConstant from "./../json/appConstant.json";
import SurveyFilter from "./SurveyFilter";
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import axios from "axios";
import moment from 'moment';
const { Text } = Typography;

const { RangePicker } = DatePicker;

const Survey = ({ onSearch, isDarkMode, onDataReceived }) => {
  const [data, setData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const [dates, setDates] = useState([]);
  const [search, setSearch] = useState("");
  const { jsonApi } = useApis();
  const imgUrl = process.env.REACT_APP_IMG_URL;
  const [dateRange, setDateRange] = useState([]);

  const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [currentSurveyToReject, setCurrentSurveyToReject] = useState(null);

  const [dateFilter, setDateFilter] = useState({
    startDate: null,
    endDate: null,
  });

  const [downloading, setDownloading] = useState(false);
  const [dateRangeError, setDateRangeError] = useState(false);

  // Ward and Mohalla states
  const [wards, setWards] = useState([]);
  const [mohallas, setMohallas] = useState([]);
  const [selectedWard, setSelectedWard] = useState(null);
  const [selectedMohalla, setSelectedMohalla] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [loadingWards, setLoadingWards] = useState(false);
  const [loadingMohallas, setLoadingMohallas] = useState(false);

  // Fetch wards data
  const fetchWards = async () => {
    setLoadingWards(true);
    try {
      const res = await jsonApi(appConstant.WardAllData, {}, null, null);
      if (!res.error && res.data?.code === 200) {
        const wardsData = res.data.data.wards || res.data.data || [];
        setWards(wardsData);
      }
    } catch (error) {
      console.error("Error fetching wards:", error);
      message.error("Failed to fetch wards");
    } finally {
      setLoadingWards(false);
    }
  };

  // Fetch mohallas data
  const fetchMohallas = async (wardId = null) => {
    setLoadingMohallas(true);
    try {
      const params = wardId ? { parent_id: wardId } : {};
      const res = await jsonApi(appConstant.MohallaWeb, params, null, null);
      if (!res.error && res.data?.code === 200) {
        const mohallasData = res.data.data || [];
        setMohallas(mohallasData);
      }
    } catch (error) {
      console.error("Error fetching mohallas:", error);
      message.error("Failed to fetch mohallas");
    } finally {
      setLoadingMohallas(false);
    }
  };

  const fetchData = async (search = "") => {
    setLoading(true);
    try {
      const params = {
        search,
        ...(dateFilter.startDate && { startDate: dateFilter.startDate }),
        ...(dateFilter.endDate && { endDate: dateFilter.endDate }),
        ...(selectedWard && { wardId: selectedWard }),
        ...(selectedMohalla && selectedMohalla !== 'all' && { mohallaId: selectedMohalla }),
        ...(selectedStatus && selectedStatus !== 'all' && { status: selectedStatus }),
      };

      const res = await jsonApi(appConstant.surveyData, params, null, null);
      if (!res.error) {
        setData(res.data.data.surveys);
        setPagination((prev) => ({
          ...prev,
          total: res.data.data.totalSurveys,
        }));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWards();
    fetchMohallas(); // Fetch all mohallas initially
  }, []);

  useEffect(() => {
    fetchData(search);
  }, [search, dateFilter, selectedWard, selectedMohalla, selectedStatus]);

  const handleView = async (record) => {
    const res = await jsonApi(appConstant.singleSurvey, { id: record?.id });
    if (!res.error) {
      setSelectedSurvey(res.data.data);
      setIsModalVisible(true);
    }
  };

  const handleTableChange = (pagination) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const handleDelete = async (userId) => {
    Modal.confirm({
      content: "Are you sure you want to delete this survey?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        setLoading(true);
        try {
          const response = await jsonApi(
            appConstant.deleteSurvey,
            { id: userId },
            null,
            "delete"
          );

          if (response?.data?.code === 200 || response?.status === 200) {
            message.success("Survey data deleted successfully");
            setData((prevUsers) =>
              prevUsers.filter((user) => user.id !== userId)
            );
          } else {
            message.error(response.message || "Failed to delete user");
          }
        } catch (error) {
          console.error("Delete User Error:", error);
          message.error("An error occurred while deleting the user");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleApproveConfirmation = (record) => {
    Modal.confirm({
      content: 'Do you want to approve this survey?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          const response = await jsonApi(
            appConstant.approveSurvey,
            { id: record.id },
            null,
            'post'
          );

          if (response?.data?.code === 200 || response?.status === 200) {
            await handleApprove(record); // Call your UI update logic
            message.success('Survey approved successfully.');
          } else {
            message.error(response?.message || 'Failed to approve survey');
          }
        } catch (error) {
          console.error('Approve Error:', error);
          message.error('An error occurred while approving the survey');
        } finally {

        }
      },
    });
  };

  const handleReject = (record) => {
    setCurrentSurveyToReject(record);
    setIsRejectModalVisible(true);
  };

  const handleApprove = (record) => {
    // Example logic to update UI or local state
    setData((prevData) =>
      prevData.map((item) =>
        item.id === record.id ? { ...item, status: 'approved' } : item
      )
    );
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      message.warning("Please enter a rejection reason.");
      return;
    }

    if (!currentSurveyToReject?.id) {
      message.error("No survey selected to reject.");
      return;
    }

    const payload = {
      reason: rejectionReason,
    };

    try {
      const response = await jsonApi(
        appConstant.rejectSurvey,
        { id: currentSurveyToReject?.id },
        payload, // Sending rejection reason in the body
        null,
        "post"
      );

      console.log("API Response:", response);

      if (response?.status === 200 || !response.error) {
        message.success("Survey rejected successfully!");

        setData((prev) =>
          prev.filter((survey) => survey.id !== currentSurveyToReject?.id)
        );
        await fetchData(); // <-- refresh list from backend
      } else {
        message.error(`Failed to reject survey. Error: ${response.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Error rejecting survey:", error);  // Detailed error logging
      message.error(`An error occurred while rejecting the survey: ${error.message}`);
    } finally {
      setIsRejectModalVisible(false);
      setRejectionReason("");
      setCurrentSurveyToReject(null);
    }
  };

  const handlePrintSurvey = () => {
    if (!selectedSurvey) {
      message.error("No survey data available to print");
      return;
    }

    // Helper function to render images
    const renderImages = (images, label) => {
      if (!images) return '';
      const imageArray = Array.isArray(images) ? images : [images];
      if (imageArray.length === 0 || (imageArray.length === 1 && !imageArray[0])) return '';
      
      return `
        <tr>
          <th>${label}</th>
          <td>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px;">
              ${imageArray.map((img, index) => img ? `
                <div style="text-align: center;">
                  <h4 style="margin-bottom: 8px;">${label} ${imageArray.length > 1 ? (index + 1) : ''}</h4>
                  <img src="${imgUrl}/${img}" style="width: 150px; height: 150px; object-fit: cover; border: 1px solid #ddd;" />
                </div>
              ` : '').join('')}
            </div>
          </td>
        </tr>
      `;
    };

    // Helper function to render two images in one row
    const renderTwoImagesInRow = (image1, label1, image2, label2, rowLabel) => {
      const hasImage1 = image1 && ((Array.isArray(image1) && image1.length > 0) || (typeof image1 === 'string' && image1.trim() !== ''));
      const hasImage2 = image2 && ((Array.isArray(image2) && image2.length > 0) || (typeof image2 === 'string' && image2.trim() !== ''));
      
      if (!hasImage1 && !hasImage2) return '';
      
      return `
        <tr>
          <th>${rowLabel}</th>
          <td>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px;">
              ${hasImage1 ? `
                <div style="text-align: center;">
                  <h4 style="margin-bottom: 8px;">${label1}</h4>
                  <img src="${imgUrl}/${Array.isArray(image1) ? image1[0] : image1}" style="width: 150px; height: 150px; object-fit: cover; border: 1px solid #ddd;" />
                </div>
              ` : '<div></div>'}
              ${hasImage2 ? `
                <div style="text-align: center;">
                  <h4 style="margin-bottom: 8px;">${label2}</h4>
                  <img src="${imgUrl}/${Array.isArray(image2) ? image2[0] : image2}" style="width: 150px; height: 150px; object-fit: cover; border: 1px solid #ddd;" />
                </div>
              ` : '<div></div>'}
            </div>
          </td>
        </tr>
      `;
    };

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
         
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              color: #333;
              line-height: 1.6;
            }
            h1 {
              text-align: center;
              color: #1890ff;
              margin-bottom: 20px;
              font-size: 28px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 10px;
              text-align: left;
              vertical-align: top;
            }
            th {
              background-color: #f0f0f0;
              font-weight: bold;
              width: 40%;
            }
            td {
              width: 60%;
            }
            tr:nth-child(even) {
              background-color: #fafafa;
            }
            .boundary-details div {
              margin: 3px 0;
            }
            ul {
              list-style: none;
              padding: 0;
              margin: 0;
            }
            ul li {
              margin: 3px 0;
            }
            @media print {
              body { padding: 10px; }
              h1 { font-size: 24px; page-break-after: avoid; }
              table { page-break-inside: auto; }
              tr { page-break-inside: avoid; page-break-after: auto; }
            }
          </style>
        </head>
        <body>
          <h1>Survey Details - ${selectedSurvey?.houseId || ""}</h1>
          <table>
            <tr><th>House ID</th><td>${selectedSurvey?.houseId || ""}</td></tr>
            <tr><th>Name</th><td>${selectedSurvey?.name || ""}</td></tr>
            <tr><th>Guardian Name</th><td>${selectedSurvey?.guardian_name || ""}</td></tr>
            <tr><th>Phone Number</th><td>${selectedSurvey?.phone_no || ""}</td></tr>
            <tr><th>Ward</th><td>${selectedSurvey?.WardName || ""}</td></tr>
            <tr><th>Mohalla</th><td>${selectedSurvey?.M_MohallaMaster?.MohallaName || ""}</td></tr>
            <tr><th>Caste</th><td>${selectedSurvey?.caste || ""}</td></tr>
            <tr><th>Relation</th><td>${selectedSurvey?.relation || ""}</td></tr>
            <tr><th>Address</th><td>${selectedSurvey?.address || ""}</td></tr>
            <tr><th>Building Rent (in Rs.)</th><td>${selectedSurvey?.building_rent || ""}</td></tr>
            <tr><th>Construction Year</th><td>${selectedSurvey?.construction_year || ""}</td></tr>
            ${
              selectedSurvey?.boundary
                ? `
              <tr>
                <th>Boundary</th>
                <td>
                  <div class="boundary-details">
                    <div><strong>East:</strong> ${selectedSurvey.boundary.East || ""}</div>
                    <div><strong>West:</strong> ${selectedSurvey.boundary.West || ""}</div>
                    <div><strong>North:</strong> ${selectedSurvey.boundary.North || ""}</div>
                    <div><strong>South:</strong> ${selectedSurvey.boundary.South || ""}</div>
                  </div>
                </td>
              </tr>
            `
                : ""
            }
            <tr><th>House Tax (in Rs.)</th><td>${selectedSurvey?.house_tax || ""}</td></tr>
            <tr><th>Area of Building Plot (in sq.m.)</th><td>${selectedSurvey?.area_of_building_plot || ""}</td></tr>
            <tr><th>Nature of Construction</th><td>${selectedSurvey?.M_NatureConstruction?.ConstructionNature || ""}</td></tr>
            <tr><th>Property Type</th><td>${selectedSurvey?.M_PropertyType?.PropertyType || ""}</td></tr>
            <tr><th>Property Status</th><td>${selectedSurvey?.M_PropertyStatus?.PStatusDetails || ""}</td></tr>
            <tr><th>Property Category</th><td>${selectedSurvey?.property_categorie?.name || ""}</td></tr>
            <tr><th>Property Commercial Category</th><td>${selectedSurvey?.M_CommercialCategory?.CommCategoryName || ""}</td></tr>
            <tr><th>Property Commercial Sub Category</th><td>${selectedSurvey?.M_CommercialSubCategory?.CommCategorySubName || ""}</td></tr>
            <tr><th>Road Rout Master</th><td>${selectedSurvey?.RoadRout?.RoadRoutDetails || ""}</td></tr>
            <tr><th>Road Rout Plot Master</th><td>${selectedSurvey?.RoadRoutPlot?.RoadRoutDetails || ""}</td></tr>
            ${
              selectedSurvey?.total_covered_area_plot
                ? `
              <tr>
                <th>Total Covered Area (in sq.ft.)</th>
                <td>
                  <ul>
                    ${Object.entries(selectedSurvey.total_covered_area_plot)
                      .map(([floor, area]) => `<li><strong>${floor}:</strong> ${area}</li>`)
                      .join("")}
                  </ul>
                </td>
              </tr>
            `
                : ""
            }
            <tr><th>Total Sum</th><td>${selectedSurvey?.total_sum || ""}</td></tr>
            <tr><th>Open Land Monthly Rent (in Rs.)</th><td>${selectedSurvey?.openlandarea_monthlyrent_yearly || ""}</td></tr>
            ${
              selectedSurvey?.annual_valuation
                ? `
              <tr>
                <th>Annual Valuation (in Rs.)</th>
                <td>
                  <ul>
                    ${Object.entries(selectedSurvey.annual_valuation)
                      .map(
                        ([key, value]) =>
                          `<li><strong>${key.charAt(0).toUpperCase() + key.slice(1)}:</strong> ${value}</li>`
                      )
                      .join("")}
                  </ul>
                </td>
              </tr>
            `
                : ""
            }
            <tr><th>Latitude</th><td>${selectedSurvey?.latitude || ""}</td></tr>
            <tr><th>Longitude</th><td>${selectedSurvey?.longitude || ""}</td></tr>
            <tr><th>Current Address</th><td>${selectedSurvey?.current_Address || ""}</td></tr>
            <tr><th>Monthly Rental Rate (in Rs.)</th><td>${selectedSurvey?.monthly_rental_rate || ""}</td></tr>
            <tr><th>No Of Floors</th><td>${selectedSurvey?.noOfFloors || ""}</td></tr>
            <tr><th>No Of Rooms</th><td>${selectedSurvey?.noOfRooms || ""}</td></tr>
            <tr><th>Toilet Available</th><td>${selectedSurvey?.toiletAvailable || ""}</td></tr>
            ${
              selectedSurvey?.toiletAvailable?.toLowerCase().trim() === "yes"
                ? `<tr><th>Toilet Type</th><td>${selectedSurvey?.toiletType || ""}</td></tr>`
                : ""
            }
            <tr><th>Water Connection</th><td>${selectedSurvey?.waterConnection || ""}</td></tr>
            <tr><th>Remarks</th><td>${selectedSurvey?.remarks || ""}</td></tr>
            <tr><th>Id Type</th><td>${selectedSurvey?.idType || ""}</td></tr>
            <tr><th>Id Number</th><td>${selectedSurvey?.idNumber || ""}</td></tr>
            ${renderTwoImagesInRow(selectedSurvey?.idPhoto, "ID Photo", selectedSurvey?.imageOfSignature, "Signature Image", "ID Photo & Signature")}
            ${renderTwoImagesInRow(selectedSurvey?.frontImageHouse, "Front House Image", selectedSurvey?.backImageHouse, "Back House Image", "Front & Back House Images")}
            ${renderTwoImagesInRow(selectedSurvey?.leftImageHouse, "Left House Image", selectedSurvey?.rightImageHouse, "Right House Image", "Left & Right House Images")}
            <tr><th>Registry Cost</th><td>${selectedSurvey?.registryCost || ""}</td></tr>
            ${renderTwoImagesInRow(selectedSurvey?.otherImageHouse, "Other House Image", selectedSurvey?.registryPhoto && !selectedSurvey.registryPhoto.endsWith('.pdf') ? selectedSurvey.registryPhoto : null, "Registry Photo", "Other House & Registry Images")}
            <tr><th>App Version</th><td>${selectedSurvey?.app_version || ""}</td></tr>
            <tr><th>Status</th><td>${selectedSurvey?.status || ""}</td></tr>
            <tr><th>Survey Approved By</th><td>${selectedSurvey?.approver?.name || ""}</td></tr>
            <tr><th>Survey Approved Date</th><td>${selectedSurvey?.approvedDate ? dayjs(selectedSurvey.approvedDate).format("DD-MM-YYYY HH:mm:ss") : ""}</td></tr>
            <tr><th>Survey Rejected By</th><td>${selectedSurvey?.rejecter?.name || ""}</td></tr>
            <tr><th>Survey Rejected Reason</th><td>${selectedSurvey?.rejectedReason || ""}</td></tr>
            <tr><th>Survey Rejected Date</th><td>${selectedSurvey?.rejectedDate ? dayjs(selectedSurvey.rejectedDate).format("DD-MM-YYYY HH:mm:ss") : ""}</td></tr>
            <tr><th>Swami/Adhyashi</th><td>${selectedSurvey?.swamiOrAdhyashi || ""}</td></tr>
            ${renderImages(selectedSurvey?.swamiOrAdhyashiImage, "Swami/Adhyashi Image")}
            <tr><th>Created At</th><td>${selectedSurvey?.created_at ? dayjs(selectedSurvey.created_at).format("DD-MM-YYYY HH:mm:ss") : ""}</td></tr>
            <tr><th>Updated At</th><td>${selectedSurvey?.updatedAt ? dayjs(selectedSurvey.updatedAt).format("DD-MM-YYYY HH:mm:ss") : ""}</td></tr>
          </table>
        </body>
      </html>
    `;

    // Create a hidden iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(printContent);
    iframeDoc.close();

    // Wait for content to load, then print
    iframe.onload = () => {
      const iframeWindow = iframe.contentWindow;
      
      // Listen for afterprint event to cleanup
      iframeWindow.onafterprint = () => {
        // Remove iframe after print dialog closes
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 100);
      };
      
      iframeWindow.focus();
      iframeWindow.print();
    };
  };

  const handleExportSurvey = async () => {
    try {
      setLoading(true);
      const params = {
        search,
        ...(dateFilter.startDate && { startDate: dateFilter.startDate }),
        ...(dateFilter.endDate && { endDate: dateFilter.endDate }),
        ...(selectedWard && selectedWard !== 'all' && { wardId: selectedWard }),
        ...(selectedMohalla && selectedMohalla !== 'all' && { mohallaId: selectedMohalla }),
        ...(selectedStatus && selectedStatus !== 'all' && { status: selectedStatus }),
        
      };

      const res = await jsonApi(appConstant.surveyData, params, null, null);
      if (!res.error && res.data?.data?.surveys) {
        const allSurveys = res.data.data.surveys;

        const formattedData = allSurveys.map(survey => ({
          "Property ID": survey.houseId || '',
          "Name": survey.name || '',
          "Guardian Name": survey.guardian_name || '',
          "Phone Number": survey.phone_no || '',
          "Ward ID": survey.M_WardMaster?.id || 'N/A',
          "Ward Name": survey.M_WardMaster?.WardName || 'N/A',
          "Mohalla ID": survey.M_MohallaMaster?.Id || 'N/A',
          "Mohalla Name": survey.M_MohallaMaster?.MohallaName || 'N/A',
          "Caste": survey.caste || '',
          "Relation": survey.relation || '',
          "Address": survey.address || '',
          "Building Rent (in Rs.)": survey.building_rent || '',
          "Construction Year": survey.construction_year || '',
          "Boundary East": survey.boundary?.East || '',
          "Boundary West": survey.boundary?.West || '',
          "Boundary North": survey.boundary?.North || '',
          "Boundary South": survey.boundary?.South || '',
          "House Tax (in Rs.)": survey.house_tax || '',
          "Area of Building Plot (in sq.m.)": survey.area_of_building_plot || '',
          "Nature of Construction ID": survey.M_NatureConstruction?.ConstNatureID || '',
          "Nature of Construction": survey.M_NatureConstruction?.ConstructionNature || '',
          "Property Type ID": survey.M_PropertyType?.Id || '',
          "Property Type": survey.M_PropertyType?.PropertyType || '',
          "Property Status ID": survey.M_PropertyStatus?.Id || '',
          "Property Status": survey.M_PropertyStatus?.PStatusDetails || '',
          "Property Category ID": survey.property_categorie?.id || '',
          "Property Category": survey.property_categorie?.name || '',
          "Property Commercial Category ID": survey.M_CommercialCategory?.Id || '',
          "Property Commercial Category": survey.M_CommercialCategory?.CommCategoryName || '',
          "Property Commercial Sub Category ID": survey.M_CommercialSubCategory?.Id || '',
          "Property Commercial Sub Category": survey.M_CommercialSubCategory?.CommCategorySubName || '',
          "Road Rout Master ID": survey.RoadRout?.Id || '',
          "Road Rout Master": survey.RoadRout?.RoadRoutDetails || '',
          "Road Rout Plot Master ID": survey.RoadRoutPlot?.Id || '',
          "Road Rout Plot Master": survey.RoadRoutPlot?.RoadRoutDetails || '',
          "Total Covered Area (Basement)": survey.total_covered_area_plot?.Basement || '',
          "Total Covered Area (First Floor)": survey.total_covered_area_plot?.FirstFloor || '',
          "Total Covered Area (Other Floor)": survey.total_covered_area_plot?.OtherFloor || '',
          "Total Covered Area (Third Floor)": survey.total_covered_area_plot?.ThirdFloor || '',
          "Total Covered Area (Ground Floor)": survey.total_covered_area_plot?.GroundFloor || '',
          "Total Covered Area (Second Floor)": survey.total_covered_area_plot?.SecondFloor || '',
          "Total Sum": survey.total_sum || '',
          "Open Land Monthly Rent (in Rs.)": survey.openlandarea_monthlyrent_yearly || '',
          "Annual Valuation Area": survey.annual_valuation?.area || '',
          "Annual Valuation Yearly Rent": survey.annual_valuation?.yearly_rent || '',
          "Annual Valuation Monthly Rate": survey.annual_valuation?.monthly_rate || '',
          "Annual Valuation Compound Rent": survey.annual_valuation?.compound_rent || '',
          "Annual Valuation Total Rent After Compound": survey.annual_valuation?.total_rent_after_compound || '',
          "Latitude": survey.latitude || '',
          "Longitude": survey.longitude || '',
          "Current Address": survey.current_Address || '',
          "Monthly Rental Rate (in Rs.)": survey.monthly_rental_rate || '',
          "No Of Floors": survey.noOfFloors || '',
          "No Of Rooms": survey.noOfRooms || '',
          "Toilet Available": survey.toiletAvailable || '',
          "Toilet Type": survey.toiletType || '',
          "Water Connection": survey.waterConnection || '',
          "Remarks": survey.remarks || '',
          "Id Type": survey.idType || '',
          "Id Number": survey.idNumber || '',
          "Status": survey.status || '',
          "Approved By Id": survey.approver?.id || '',
          "Approved By Name": survey.approver?.name || '',
          "Approved Date": survey.approvedDate? dayjs(survey.approvedDate).format('DD-MM-YYYY HH:mm:ss') : '',
          "Rejected By Id": survey.rejecter?.id || '',
          "Rejected By Name": survey.rejecter?.name || '',
          "Rejected Reason": survey.rejectedReason || '',
          "Rejected Date": survey.rejectedDate? dayjs(survey.rejectedDate).format('DD-MM-YYYY HH:mm:ss') : '',
          "Registry Cost": survey.registryCost || '',
          "Id Photo": Array.isArray(survey.idPhoto) ? survey.idPhoto.join(', ') : '',
          "Registry Photo": survey.registryPhoto,
          "Created At": survey.created_at ? dayjs(survey.created_at).format('DD-MM-YYYY HH:mm:ss') : '',
          "Created By ": survey.user.name || '',
          "App Version": survey.app_version || '',
          "Front Image House": survey.frontImageHouse || '',
          "Back Image House": survey.backImageHouse || '',
          "Left Image House": survey.leftImageHouse || '',
          "Right Image House": survey.rightImageHouse || '',
          "Other Image House": survey.otherImageHouse || '',
          "Signature Image": survey.imageOfSignature || '',
          "Swami/Adhyashi": survey.swamiOrAdhyashi || '',
          "Swami/Adhyashi Image": survey.swamiOrAdhyashiImage || '',
        }));

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Surveys");

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });

        saveAs(dataBlob, 'survey_data.xlsx');
        message.success("Survey data exported to Excel successfully!");
      } else {
        message.error("Failed to fetch survey data for export.");
      }
    } catch (error) {
      console.error("Export Survey Error:", error);
      message.error("An error occurred during export.");
    } finally {
      setLoading(false);
    }
  };

  
  const downloadImages = async () => {
    try {
      if (!dateRange || dateRange.length !== 2) {
        message.warning('Please select a valid date range for downloading survey images.');
        return;
      }

      const start = dateRange[0];
      const end = dateRange[1];
      const dayDiff = end.diff(start, 'days');

      if (dayDiff > 5) {
        setDateRangeError(true);
        message.error('Date range for download survey images should not exceed 5 days.');
        return;


      }

      setDateRangeError(false);
      setDownloading(true);

      const startDate = start.format('YYYY-MM-DD');
      const endDate = end.format('YYYY-MM-DD');
      const fileName = `survey_images(${start.format('DD-MM-YYYY')}-${end.format('DD-MM-YYYY')}).zip`;

      const ENDPOINT = process.env.REACT_APP_BASEURL;

      const response = await axios.get(
        `${ENDPOINT}${appConstant.DownloadImage.path}?startDate=${startDate}&endDate=${endDate}`,
        { responseType: 'blob' }
      );

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success('Download completed successfully!');
    } catch (error) {
      console.error('Error downloading ZIP:', error);

      // Handle specific error message from backend
      if (error.response && error.response.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('There was an error downloading the ZIP file. Please try again later.');
      }
    } finally {
      setDownloading(false);
    }
  };

  const columns = [
    {
      title: "S.No",
      key: "serialNumber",
      render: (_, __, index) => {
        const { current, pageSize } = pagination;
        return (current - 1) * pageSize + index + 1;
      },

    },
    {
      title: "House ID",
      dataIndex: "houseId",
      key: "houseId",

    },
    {
      title: "Ward",
      dataIndex: "M_WardMaster",
      key: "WardName",
      sorter: (a, b) => a.M_WardMaster.WardName.localeCompare(b.M_WardMaster.WardName),
      render: (M_WardMaster) => M_WardMaster?.WardName || "N/A",
    },
    {
      title: "Mohalla",
      dataIndex: "M_MohallaMaster",
      key: "MohallaName",
      sorter: (a, b) => a.M_MohallaMaster.MohallaName.localeCompare(b.M_MohallaMaster.MohallaName),
      render: (M_MohallaMaster) => M_MohallaMaster?.MohallaName || "N/A",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Guardian Name",
      dataIndex: "guardian_name",
      key: "guardian_name",
      sorter: (a, b) => a.guardian_name.localeCompare(b.guardian_name),
    },
    {
      title: "Phone Number",
      dataIndex: "phone_no",
      key: "phone_no",
      sorter: (a, b) => {
        const phoneA = a.phone_no || "";
        const phoneB = b.phone_no || "";
        return phoneA.localeCompare(phoneB);
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (status) => {
        let color = "";
        let displayText = "";

        switch (status?.toLowerCase()) {
          case "in-progress":
            color = "#1677ff"; // dark blue
            displayText = "In-Progress";
            break;
          case "rejected":
            color = "#ff4d4f"; // dark red
            displayText = "Rejected";
            break;
          case "approved":
            color = "rgb(82, 196, 26)"; // dark green
            displayText = "Approved";
            break;
          default:
            color = "#6B7280"; // gray
            displayText = status;
        }

        return (
          <span style={{ color, textTransform: "capitalize" }}>
            {displayText}
          </span>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      render: (date) => {
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
      },
    },
    {
      title: "Created By",
      dataIndex: "user",
      key: "createdBy",
      sorter: (a, b) => a.user.name.localeCompare(b.user.name),
      render: (user) => user?.name || "N/A",
    },
    {
      title: "Actions",
      key: "action",
      render: (_, record) => (
        <div style={{ textAlign: 'left' }}>
          <Space size="middle">
            <Button type="link" onClick={() => handleView(record)}>
              View
            </Button>
            <Button type="link" danger onClick={() => handleDelete(record.id)}>
              Delete
            </Button>
                          {record.status?.toLowerCase() === "in-progress" && (
              <>
                <Button type="link" onClick={() => handleApproveConfirmation(record)}>
                  Approve
                </Button>
                <Button type="link" onClick={() => handleReject(record)}>
                  Reject
                </Button>
              </>
            )}
          </Space>
        </div>
      ),
      align: "center",
    }
  ];

  const handleFilteredData = (data) => {
    if (data && Array.isArray(data.data)) {
      setData(data.data);
      setPagination((prev) => ({
        ...prev,
        total: data.total,
      }));
    }
  };

  const handleSearch = async () => {
    const [startDate, endDate] = dates || [];
    if (!startDate || !endDate) {
      message.warning("Please select both start and end dates");
      return;
    }

    setLoading(true);
    try {
      const formattedStartDate = startDate.format("YYYY-MM-DD");
      const formattedEndDate = endDate.format("YYYY-MM-DD");

      setDateFilter({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });

      const response = await jsonApi(
        appConstant.surveyData,
        {
          startDate: formattedStartDate,
          endDate: formattedEndDate
        },
        null,
        null
      );

      if (response?.data?.code === 200 && response?.data?.status === "success") {
        if (response.data.data.surveys.length === 0) {
          message.error('No Data Found');
        } else {
          message.success('Data Filtered Successfully');
        }
      }
    } catch (error) {
      console.error("Error in date filter:", error);
      message.error("Failed to apply date filter");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setDates(null);
    setDateFilter({ startDate: null, endDate: null });
    setSelectedWard(null);
    setSelectedMohalla(null);
    setSelectedStatus(null);
    setSearch("");
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleDateChange = (values) => {
    setDates(values);
    if (!values) {
      handleClear();
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ padding: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <h2>Surveys</h2>
            <Space size="middle" >
              <RangePicker
                style={{ width: "200px" }}
                onChange={handleDateChange}
                format="YYYY-MM-DD"
                placeholder={["Start Date", "End Date"]}
                value={dates}
                allowClear={true}
                disabledDate={(current) => current && current > moment().endOf("day")}
              />

              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
              >
                Search
              </Button>

              {/* Ward Dropdown */}
              <Select
                placeholder="Select Ward"
                style={{ width: 150 }}
                value={selectedWard}
                onChange={(value) => {
                  setSelectedWard(value);
                  setSelectedMohalla('all'); // Set to "All Mohallas" when ward changes
                  setPagination((prev) => ({ ...prev, current: 1 }));
                  // Fetch mohallas for the selected ward
                  if (value) {
                    fetchMohallas(value);
                  } else {
                    // If no ward selected, fetch all mohallas
                    fetchMohallas();
                  }
                }}
                allowClear
                loading={loadingWards}
                showSearch
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {wards.map((ward) => (
                  <Select.Option key={ward.Id} value={ward.Id}>
                    {ward.WardName}
                  </Select.Option>
                ))}
              </Select>

              {/* Mohalla Dropdown */}
              <Select
                placeholder="Select Mohalla"
                style={{ width: 150 }}
                value={selectedMohalla}
                onChange={(value) => {
                  setSelectedMohalla(value);
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                allowClear
                loading={loadingMohallas}
                showSearch
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
              >
                <Select.Option key="all" value="all">
                  All
                </Select.Option>
                {mohallas.map((mohalla) => (
                  <Select.Option key={mohalla.Id} value={mohalla.Id}>
                    {mohalla.MohallaName}
                  </Select.Option>
                ))}
              </Select>

              {/* Status Dropdown */}
              <Select
                placeholder="Select Status"
                style={{ width: 150 }}
                value={selectedStatus}
                onChange={(value) => {
                  setSelectedStatus(value);
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                allowClear
              >
                <Select.Option key="all" value="all">
                  All
                </Select.Option>
                <Select.Option key="In-progress" value="In-progress">
                  In-Progress
                </Select.Option>
                <Select.Option key="rejected" value="Rejected">
                  Rejected
                </Select.Option>
                <Select.Option key="approved" value="Approved">
                  Approved
                </Select.Option>
              </Select>


            </Space>

          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px", marginBottom: "30px" }}>
            <Text
              style={{
                color: 'rgba(0, 0, 0, 0.85)',
                padding: '4px 8px',
                borderRadius: '4px',
                display: 'inline-block',
                fontSize: '13px',
                border: dateRangeError ? '2px solid #ff4d4f' : 'none',
              }}
            >
              * Please select a date range to download images (maximum 5 days)
            </Text>
            <Space align="end" size={16}>
              <RangePicker
                style={{ width: 200 }}
                onChange={(dates) => {
                  setDateRange(dates);
                  setDateRangeError(false);
                }}
                format="YYYY-MM-DD"
                disabledDate={(current) => current && current > moment().endOf("day")}
              />
              <Button
                type="primary"
                onClick={downloadImages}
                style={{
                  backgroundColor: '#52c41a',
                  borderColor: '#52c41a',
                  color: downloading ? '#000' : '#fff'
                }}
                loading={downloading}
                disabled={downloading}
              >
                {downloading ? 'Downloading...' : 'Download Images'}
              </Button>
              <Button
                type="primary"
                onClick={handleExportSurvey}
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              >
                Export Survey
              </Button>

              <Input
                placeholder="Search surveys..."
                prefix={<SearchOutlined />}
                style={{ width: 160 }}
                value={search}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearch(value);
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                suffix={search && (
                  <Button
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={() => {
                      setSearch('');
                      setPagination((prev) => ({ ...prev, current: 1 }));
                    }}
                  />
                )}
              />
            </Space>
          </div>
        </div>
      </div>


      <Card bodyStyle={{ padding: 0 }}>
        {loading ? (
          <Spin
            size="large"
            style={{ display: "block", textAlign: "center", margin: "20px 0" }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            pagination={pagination}
            onChange={handleTableChange}
          />
        )}
      </Card>

      {/* Survey Detail Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '24px' }}>
            <span>Survey Details</span>
            <Button 
              type="primary" 
              icon={<PrinterOutlined />} 
              onClick={handlePrintSurvey}
              size="middle"
            >
              Print
            </Button>
          </div>
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[]}
        width={800}
      >
        {selectedSurvey && (
          <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
            <Descriptions bordered column={1}>

              <Descriptions.Item label="House ID">
                {selectedSurvey.houseId}
              </Descriptions.Item>

              <Descriptions.Item label="Name">
                {selectedSurvey.name}
              </Descriptions.Item>
              <Descriptions.Item label="Guardian Name">
                {selectedSurvey.guardian_name}
              </Descriptions.Item>

              <Descriptions.Item label="Phone Number">
                {selectedSurvey.phone_no}
              </Descriptions.Item>
              <Descriptions.Item label="Ward">
                {selectedSurvey.WardName}
              </Descriptions.Item>

              <Descriptions.Item label="Mohalla">
                {selectedSurvey.M_MohallaMaster.MohallaName}
              </Descriptions.Item>

              <Descriptions.Item label="Caste">
                {selectedSurvey.caste}
              </Descriptions.Item>
              <Descriptions.Item label="Relation">
                {selectedSurvey.relation}
              </Descriptions.Item>
              <Descriptions.Item label="Address">
                {selectedSurvey.address}
              </Descriptions.Item>
              <Descriptions.Item label="Building Rent (in Rs.)">
                {selectedSurvey.building_rent}
              </Descriptions.Item>
              <Descriptions.Item label="Construction Year">
                {selectedSurvey.construction_year}
              </Descriptions.Item>
              {selectedSurvey && selectedSurvey.boundary && (
                <Descriptions.Item label="Boundary">
                  <div>
                    <div><strong>East:</strong> {selectedSurvey.boundary.East}</div>
                    <div><strong>West:</strong> {selectedSurvey.boundary.West}</div>
                    <div><strong>North:</strong> {selectedSurvey.boundary.North}</div>
                    <div><strong>South:</strong> {selectedSurvey.boundary.South}</div>
                  </div>
                </Descriptions.Item>
              )}

              <Descriptions.Item label="House Tax (in Rs.)">
                {selectedSurvey.house_tax}
              </Descriptions.Item>
              <Descriptions.Item label="Area of Building Plot (in sq.m.)">
                {selectedSurvey.area_of_building_plot}
              </Descriptions.Item>
              <Descriptions.Item label="Nature of Construction">
                {selectedSurvey?.M_NatureConstruction?.ConstructionNature}
              </Descriptions.Item>

              <Descriptions.Item label="Property Type">
                {selectedSurvey.M_PropertyType.PropertyType}
              </Descriptions.Item>
              <Descriptions.Item label="Property Status">
                {selectedSurvey.M_PropertyStatus.PStatusDetails}
              </Descriptions.Item>
              <Descriptions.Item label="Property Category">
                {selectedSurvey.property_categorie.name}
              </Descriptions.Item>

              <Descriptions.Item label="Property Commercial Category">
                {selectedSurvey?.M_CommercialCategory?.CommCategoryName}
              </Descriptions.Item>

              <Descriptions.Item label="Property Commercial Sub Category">
                {selectedSurvey?.M_CommercialSubCategory?.CommCategorySubName}
              </Descriptions.Item>

              <Descriptions.Item label="Road Rout Master">
                {selectedSurvey?.RoadRout?.RoadRoutDetails}
              </Descriptions.Item>

              <Descriptions.Item label="Road Rout Plot Master">
                {selectedSurvey?.RoadRoutPlot?.RoadRoutDetails}
              </Descriptions.Item>

              <Descriptions.Item label="Total Covered Area (in sq.ft.)">
                {Object.entries(selectedSurvey.total_covered_area_plot).map(
                  ([floor, area], index) => (
                    <li key={index}>
                      <strong>{floor}:</strong> {area}
                    </li>
                  )
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Total Sum">
                {selectedSurvey.total_sum}
              </Descriptions.Item>
              <Descriptions.Item label="Open Land Monthly Rent (in Rs.)">
                {selectedSurvey.openlandarea_monthlyrent_yearly}
              </Descriptions.Item>
              <Descriptions.Item label="Annual Valuation (in Rs.)">
                {Object.entries(selectedSurvey.annual_valuation).map(
                  ([key, value], index) => (
                    <li key={index}>
                      <strong>
                        {key.charAt(0).toUpperCase() + key.slice(1)}:
                      </strong>{" "}
                      {value}
                    </li>
                  )
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Latitude">
                {selectedSurvey.latitude}
              </Descriptions.Item>
              <Descriptions.Item label="Longitude">
                {selectedSurvey.longitude}
              </Descriptions.Item>
              <Descriptions.Item label="Current Address">
                {selectedSurvey.current_Address}
              </Descriptions.Item>
              <Descriptions.Item label="Monthly Rental Rate (in Rs.)">
                {selectedSurvey.monthly_rental_rate}
              </Descriptions.Item>

              <Descriptions.Item label="No Of Floors">
                {selectedSurvey.noOfFloors}
              </Descriptions.Item>

              <Descriptions.Item label="No Of Rooms">
                {selectedSurvey.noOfRooms}
              </Descriptions.Item>

              <Descriptions.Item label="Toilet Available">
                {selectedSurvey.toiletAvailable}
              </Descriptions.Item>

              {selectedSurvey.toiletAvailable?.toLowerCase().trim() === "yes" && (
                <Descriptions.Item label="Toilet Type">
                  {selectedSurvey.toiletType}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Water Connection">
                {selectedSurvey.waterConnection}
              </Descriptions.Item>

              <Descriptions.Item label="Remarks">
                {selectedSurvey.remarks}
              </Descriptions.Item>

              <Descriptions.Item label="Id Type">
                {selectedSurvey.idType}
              </Descriptions.Item>

              <Descriptions.Item label="Id Number">
                {selectedSurvey.idNumber}
              </Descriptions.Item>

              {(
                (typeof selectedSurvey.idPhoto === "string" &&
                  selectedSurvey.idPhoto.trim() !== "") ||
                (Array.isArray(selectedSurvey.idPhoto) &&
                  selectedSurvey.idPhoto.length > 0)
              ) && (
                  <Descriptions.Item label="Id Photo">
                    {typeof selectedSurvey.idPhoto === "string" ? (
                      <img
                        src={`${imgUrl}/${selectedSurvey.idPhoto}`}
                        alt="Id Photo"
                        style={{
                          width: "200px",
                          height: "200px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Space>
                        {selectedSurvey.idPhoto.map((image, index) => (
                          <img
                            key={index}
                            src={`${imgUrl}/${image}`}
                            alt={`Id Photo ${index + 1}`}
                            style={{
                              width: "200px",
                              height: "200px",
                              objectFit: "cover",
                            }}
                          />
                        ))}
                      </Space>
                    )}
                  </Descriptions.Item>
                )}

              {(
                (typeof selectedSurvey.imageOfSignature === "string" &&
                  selectedSurvey.imageOfSignature.trim() !== "") ||
                (Array.isArray(selectedSurvey.imageOfSignature) &&
                  selectedSurvey.imageOfSignature.length > 0)
              ) && (
                  <Descriptions.Item label="Signature Image">
                    {typeof selectedSurvey.imageOfSignature === "string" ? (
                      <img
                        src={`${imgUrl}/${selectedSurvey.imageOfSignature}`}
                        alt="Signature"
                        style={{
                          width: "200px",
                          height: "200px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Space>
                        {selectedSurvey.imageOfSignature.map((image, index) => (
                          <img
                            key={index}
                            src={`${imgUrl}/${image}`}
                            alt={`Signature ${index + 1}`}
                            style={{
                              width: "200px",
                              height: "200px",
                              objectFit: "cover",
                            }}
                          />
                        ))}
                      </Space>
                    )}
                  </Descriptions.Item>
                )}



              {(
                (typeof selectedSurvey.frontImageHouse === "string" &&
                  selectedSurvey.frontImageHouse.trim() !== "") ||
                (Array.isArray(selectedSurvey.frontImageHouse) &&
                  selectedSurvey.frontImageHouse.length > 0)
              ) && (
                  <Descriptions.Item label="Front House Image">
                    {typeof selectedSurvey.frontImageHouse === "string" ? (
                      <img
                        src={`${imgUrl}/${selectedSurvey.frontImageHouse}`}
                        alt="Front House"
                        style={{
                          width: "200px",
                          height: "200px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Space>
                        {selectedSurvey.frontImageHouse.map((image, index) => (
                          <img
                            key={index}
                            src={`${imgUrl}/${image}`}
                            alt={`Front House ${index + 1}`}
                            style={{
                              width: "200px",
                              height: "200px",
                              objectFit: "cover",
                            }}
                          />
                        ))}
                      </Space>
                    )}
                  </Descriptions.Item>
                )}


              {(
                (typeof selectedSurvey.backImageHouse === "string" &&
                  selectedSurvey.backImageHouse.trim() !== "") ||
                (Array.isArray(selectedSurvey.backImageHouse) &&
                  selectedSurvey.backImageHouse.length > 0)
              ) && (
                  <Descriptions.Item label="Back House Image">
                    {typeof selectedSurvey.backImageHouse === "string" ? (
                      <img
                        src={`${imgUrl}/${selectedSurvey.backImageHouse}`}
                        alt="Back House"
                        style={{
                          width: "200px",
                          height: "200px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Space>
                        {selectedSurvey.backImageHouse.map((image, index) => (
                          <img
                            key={index}
                            src={`${imgUrl}/${image}`}
                            alt={`Back House ${index + 1}`}
                            style={{
                              width: "200px",
                              height: "200px",
                              objectFit: "cover",
                            }}
                          />
                        ))}
                      </Space>
                    )}
                  </Descriptions.Item>
                )}

              {(
                (typeof selectedSurvey.leftImageHouse === "string" &&
                  selectedSurvey.leftImageHouse.trim() !== "") ||
                (Array.isArray(selectedSurvey.leftImageHouse) &&
                  selectedSurvey.leftImageHouse.length > 0)
              ) && (
                  <Descriptions.Item label="Left House Image">
                    {typeof selectedSurvey.leftImageHouse === "string" ? (
                      <img
                        src={`${imgUrl}/${selectedSurvey.leftImageHouse}`}
                        alt="Left House"
                        style={{
                          width: "200px",
                          height: "200px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Space>
                        {selectedSurvey.leftImageHouse.map((image, index) => (
                          <img
                            key={index}
                            src={`${imgUrl}/${image}`}
                            alt={`Left House ${index + 1}`}
                            style={{
                              width: "200px",
                              height: "200px",
                              objectFit: "cover",
                            }}
                          />
                        ))}
                      </Space>
                    )}
                  </Descriptions.Item>
                )}


              {(
                (typeof selectedSurvey.rightImageHouse === "string" &&
                  selectedSurvey.rightImageHouse.trim() !== "") ||
                (Array.isArray(selectedSurvey.rightImageHouse) &&
                  selectedSurvey.rightImageHouse.length > 0)
              ) && (
                  <Descriptions.Item label="Right House Image">
                    {typeof selectedSurvey.rightImageHouse === "string" ? (
                      <img
                        src={`${imgUrl}/${selectedSurvey.rightImageHouse}`}
                        alt="Right House"
                        style={{
                          width: "200px",
                          height: "200px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Space>
                        {selectedSurvey.rightImageHouse.map((image, index) => (
                          <img
                            key={index}
                            src={`${imgUrl}/${image}`}
                            alt={`Right House ${index + 1}`}
                            style={{
                              width: "200px",
                              height: "200px",
                              objectFit: "cover",
                            }}
                          />
                        ))}
                      </Space>
                    )}
                  </Descriptions.Item>
                )}


              {(
                (typeof selectedSurvey.otherImageHouse === "string" &&
                  selectedSurvey.otherImageHouse.trim() !== "") ||
                (Array.isArray(selectedSurvey.otherImageHouse) &&
                  selectedSurvey.otherImageHouse.length > 0)
              ) && (
                  <Descriptions.Item label="Other House Image">
                    {typeof selectedSurvey.otherImageHouse === "string" ? (
                      <img
                        src={`${imgUrl}/${selectedSurvey.otherImageHouse}`}
                        alt="Other House"
                        style={{
                          width: "200px",
                          height: "200px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Space>
                        {selectedSurvey.otherImageHouse.map((image, index) => (
                          <img
                            key={index}
                            src={`${imgUrl}/${image}`}
                            alt={`Other House ${index + 1}`}
                            style={{
                              width: "200px",
                              height: "200px",
                              objectFit: "cover",
                            }}
                          />
                        ))}
                      </Space>
                    )}
                  </Descriptions.Item>
                )}
              <Descriptions.Item label="Registry Cost">
                {selectedSurvey.registryCost}
              </Descriptions.Item>


              {(
                (typeof selectedSurvey.registryPhoto === "string" && selectedSurvey.registryPhoto.trim() !== "") ||
                (Array.isArray(selectedSurvey.registryPhoto) && selectedSurvey.registryPhoto.length > 0)
              ) && (
                  <Descriptions.Item label="Registry Photo">
                    {typeof selectedSurvey.registryPhoto === "string" ? (
                      selectedSurvey.registryPhoto.endsWith(".pdf") ? (
                        <a href={`${imgUrl}/${selectedSurvey.registryPhoto}`} target="_blank" rel="noopener noreferrer">
                          {selectedSurvey.registryPhoto}
                        </a>
                      ) : (
                        <img
                          src={`${imgUrl}/${selectedSurvey.registryPhoto}`}
                          alt="House"
                          style={{ width: "200px", height: "200px", objectFit: "cover" }}
                        />
                      )
                    ) : (
                      <Space>
                        {selectedSurvey.registryPhoto.map((file, index) =>
                          file.endsWith(".pdf") ? (
                            <a
                              key={index}
                              href={`${imgUrl}/${file}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ display: "block" }}
                            >
                              {file}
                            </a>
                          ) : (
                            <img
                              key={index}
                              src={`${imgUrl}/${file}`}
                              alt={`House ${index + 1}`}
                              style={{ width: "200px", height: "200px", objectFit: "cover" }}
                            />
                          )
                        )}
                      </Space>
                    )}
                  </Descriptions.Item>
                )}
              <Descriptions.Item label="Created At">
                {selectedSurvey.created_at ? dayjs(selectedSurvey.created_at).format('DD-MM-YYYY HH:mm:ss') : ''}
              </Descriptions.Item>

              <Descriptions.Item label="Updated At">
                {selectedSurvey.updatedAt ? dayjs(selectedSurvey.updatedAt).format('DD-MM-YYYY HH:mm:ss') : ''}
              </Descriptions.Item>

              <Descriptions.Item label="App Version">
                {selectedSurvey.app_version}
              </Descriptions.Item>

              <Descriptions.Item label="Status">
                {(() => {
                  let color = "";
                  let displayText = "";

                  switch (selectedSurvey.status?.toLowerCase()) {
                    case "in-progress":
                      color = "#1677ff"; // dark blue
                      displayText = "In-Progress";
                      break;
                    case "rejected":
                      color = "#ff4d4f"; // dark red
                      displayText = "Rejected";
                      break;
                    case "approved":
                      color = "rgb(82, 196, 26)"; // dark green
                      displayText = "Approved";
                      break;
                    default:
                      color = "#6B7280"; // default gray
                      displayText = selectedSurvey.status;
                  }

                  return (
                    <span style={{ color, textTransform: "capitalize" }}>
                      {displayText}
                    </span>
                  );
                })()}
              </Descriptions.Item>

              <Descriptions.Item label="Survey Approved By">
                {selectedSurvey.approver?.name}
              </Descriptions.Item>
              <Descriptions.Item label="Survey Approved Date">
                {selectedSurvey.approvedDate ? dayjs(selectedSurvey.approvedDate).format('DD-MM-YYYY HH:mm:ss') : ''}
              </Descriptions.Item>
              <Descriptions.Item label="Survey Rejected By">
                {selectedSurvey.rejecter?.name}
              </Descriptions.Item>
              <Descriptions.Item label="Survey Rejected Reason">
                <span style={{ color: "#B91C1C" }}>
                  {selectedSurvey.rejectedReason || ""}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Survey Rejected Date">
                {selectedSurvey.rejectedDate ? dayjs(selectedSurvey.rejectedDate).format('DD-MM-YYYY HH:mm:ss') : ''}
              </Descriptions.Item>

              <Descriptions.Item label="Swami/Adhyashi">
                {selectedSurvey.swamiOrAdhyashi}
              </Descriptions.Item>

                {(
                (typeof selectedSurvey.swamiOrAdhyashiImage === "string" &&
                  selectedSurvey.swamiOrAdhyashiImage.trim() !== "") ||
                (Array.isArray(selectedSurvey.swamiOrAdhyashiImage) &&
                  selectedSurvey.swamiOrAdhyashiImage.length > 0)
              ) && (
                  <Descriptions.Item label="Swami/Adhyashi Image">
                    {typeof selectedSurvey.swamiOrAdhyashiImage === "string" ? (
                      <img
                        src={`${imgUrl}/${selectedSurvey.swamiOrAdhyashiImage}`}
                        alt="Image"
                        style={{
                          width: "200px",
                          height: "200px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Space>
                        {selectedSurvey.swamiOrAdhyashiImage.map((image, index) => (
                          <img
                            key={index}
                            src={`${imgUrl}/${image}`}
                            alt={` Image ${index + 1}`}
                            style={{
                              width: "200px",
                              height: "200px",
                              objectFit: "cover",
                            }}
                          />
                        ))}
                      </Space>
                    )}
                  </Descriptions.Item>
                )}

            </Descriptions>
          </div>
        )}
      </Modal>


      {/* Reject Modal */}
      <Modal
        title="Reject Survey Reason"
        open={isRejectModalVisible}
        onCancel={() => {
          setIsRejectModalVisible(false);
          setRejectionReason("");
        }}
        onOk={handleRejectSubmit}
        okText="Submit"
      >
        <Input.TextArea
          rows={4}
          placeholder="Enter reason for rejection"
          value={rejectionReason}
          onChange={(e) => {
            const input = e.target.value;
            const alphabetOnly = input.replace(/[^a-zA-Z\s]/g, ''); // allows alphabets and spaces
            setRejectionReason(alphabetOnly);
          }}
        />
      </Modal>
    </div>
  );
};

export default Survey;